import { AdvisorsPaths } from '@src/components/sco/Advisors/paths'
import { SESSION_STATUS } from '@src/components/sco/Advisors/utils/sessionStatus'
import { IconType } from 'react-icons'
import { LuCalendarClock, LuCircleAlert, LuHistory, LuVideo } from 'react-icons/lu'

export type SessionBucket = 'all' | 'action' | 'upcoming' | 'past'

export type SessionsSectionKey = SessionBucket | 'book'

export type SessionsNavItem = {
  key: SessionsSectionKey
  title: string
  description: string
  href: string
  icon: IconType
  statusKeys?: string[]
  palette?: string
}

export const SESSION_BUCKET_LABELS: Record<SessionBucket, string> = {
  all: 'All',
  action: 'Needs action',
  upcoming: 'Upcoming',
  past: 'Past',
}

export const SESSION_BUCKET_STATUS_KEYS: Record<SessionBucket, string[] | undefined> = {
  all: undefined,
  action: [SESSION_STATUS.pending, SESSION_STATUS.verifyWithExpert, SESSION_STATUS.verifyWithSeeker],
  upcoming: [SESSION_STATUS.scheduleConfirmed, SESSION_STATUS.live],
  past: [SESSION_STATUS.completed, SESSION_STATUS.canceled, SESSION_STATUS.expired, SESSION_STATUS.timedOut],
}

export const parseSessionBucket = (value: unknown): SessionBucket => {
  const raw = String(Array.isArray(value) ? value[0] : (value ?? '')).toLowerCase()
  return raw === 'action' || raw === 'upcoming' || raw === 'past' ? raw : 'all'
}

export const sessionBucketHref = (paths: AdvisorsPaths, bucket: SessionBucket): string =>
  paths.withQuery(paths.sessions, { bucket: bucket === 'all' ? undefined : bucket })

/**
 * Sessions sub-sections, in rail order.
 *
 * Bucket views are the list filters. Rates and payouts live under
 * My Advisor Profile → Settings. Book a Session is the rail CTA, not a nav row.
 */
export const sessionsNavItems = (paths: AdvisorsPaths): SessionsNavItem[] => [
  {
    key: 'all',
    title: SESSION_BUCKET_LABELS.all,
    description: 'Book, confirm times, and join your paid video sessions.',
    href: sessionBucketHref(paths, 'all'),
    icon: LuVideo,
    statusKeys: SESSION_BUCKET_STATUS_KEYS.all,
    palette: 'green',
  },
  {
    key: 'action',
    title: SESSION_BUCKET_LABELS.action,
    description: 'When a counterpart proposes times, those sessions land here.',
    href: sessionBucketHref(paths, 'action'),
    icon: LuCircleAlert,
    statusKeys: SESSION_BUCKET_STATUS_KEYS.action,
    palette: 'yellow',
  },
  {
    key: 'upcoming',
    title: SESSION_BUCKET_LABELS.upcoming,
    description: 'Confirmed and live sessions will show up here so you can join on time.',
    href: sessionBucketHref(paths, 'upcoming'),
    icon: LuCalendarClock,
    statusKeys: SESSION_BUCKET_STATUS_KEYS.upcoming,
    palette: 'green',
  },
  {
    key: 'past',
    title: SESSION_BUCKET_LABELS.past,
    description: 'Completed and canceled sessions appear here, including ratings.',
    href: sessionBucketHref(paths, 'past'),
    icon: LuHistory,
    statusKeys: SESSION_BUCKET_STATUS_KEYS.past,
    palette: 'gray',
  },
]
